/**
 * IDW (Inverse Distance Weighting) — расчёт интерполированных значений
 * на основе разреженных точек (comuni) для тепловой карты.
 *
 * Подход: для каждой точки query (lat, lon) ищем K ближайших comuni в
 * радиусе maxRadiusDeg, считаем взвешенное среднее их значений с весом
 * 1/d^power. Это даёт плавный градиент, где ближайшая comune доминирует.
 *
 * Дистанции: kdbush работает в Euclidean, поэтому масштабируем долготу
 * на cos(lat) — это equirectangular-проекция, точная для Италии в пределах
 * ±0.5% дистанций. Достаточно для тепловой карты на уровне страны.
 */

import KDBush from 'kdbush';

export interface IdwPoint {
  lat: number;
  lon: number;
  value: number | null;
}

export interface IdwIndex {
  tree: KDBush;
  points: IdwPoint[];
  lonScale: number;
}

// Италия центр ≈ 43°N — на этой широте 1° lon = cos(43°) × 1° lat.
const ITALY_LAT_MID = 43;
const LON_SCALE = Math.cos((ITALY_LAT_MID * Math.PI) / 180);

export function buildIdwIndex(points: IdwPoint[]): IdwIndex {
  const tree = new KDBush(points.length);
  for (const p of points) tree.add(p.lat, p.lon * LON_SCALE);
  tree.finish();
  return { tree, points, lonScale: LON_SCALE };
}

interface IdwOptions {
  K?: number;          // макс число соседей в усреднении
  maxRadiusDeg?: number; // радиус поиска (в lat-degrees, 1°≈111km)
  power?: number;      // степень обратного расстояния
}

/**
 * Возвращает интерполированное значение в точке (lat, lon).
 * null — если в радиусе нет comuni с данными.
 */
export function idwAt(
  lat: number,
  lon: number,
  index: IdwIndex,
  opts: IdwOptions = {},
): number | null {
  const { K = 6, maxRadiusDeg = 0.3, power = 2 } = opts;
  const { tree, points, lonScale } = index;
  const qx = lat;
  const qy = lon * lonScale;
  const candidateIds = tree.within(qx, qy, maxRadiusDeg);
  if (candidateIds.length === 0) return null;

  // Собираем (distance, value), фильтруем null-значения.
  const items: Array<{ d: number; v: number }> = [];
  for (const id of candidateIds) {
    const p = points[id];
    if (p.value === null || !isFinite(p.value)) continue;
    const dx = p.lat - lat;
    const dy = (p.lon - lon) * lonScale;
    const d = Math.sqrt(dx * dx + dy * dy);
    items.push({ d, v: p.value });
  }
  if (items.length === 0) return null;

  // K ближайших.
  items.sort((a, b) => a.d - b.d);
  const nearest = items.length > K ? items.slice(0, K) : items;

  let totalW = 0;
  let totalV = 0;
  for (const { d, v } of nearest) {
    if (d < 1e-9) return v; // точное попадание в центроид
    const w = 1 / Math.pow(d, power);
    totalV += w * v;
    totalW += w;
  }
  return totalV / totalW;
}

/**
 * Bilinear-интерполяция значения из квадратной сетки grid[gridSize × gridSize].
 * fx, fy в диапазоне [0, gridSize-1].
 * null-cells обрабатываются: если все 4 угла null → null; иначе игнорим null
 * и усредняем доступные.
 */
export function bilinearSample(
  grid: Array<number | null>,
  gridSize: number,
  fx: number,
  fy: number,
): number | null {
  const ix = Math.floor(fx);
  const iy = Math.floor(fy);
  const tx = fx - ix;
  const ty = fy - iy;
  const ix2 = Math.min(ix + 1, gridSize - 1);
  const iy2 = Math.min(iy + 1, gridSize - 1);

  const v00 = grid[iy * gridSize + ix];
  const v01 = grid[iy * gridSize + ix2];
  const v10 = grid[iy2 * gridSize + ix];
  const v11 = grid[iy2 * gridSize + ix2];

  const top = lerpNullSafe(v00, v01, tx);
  const bot = lerpNullSafe(v10, v11, tx);
  return lerpNullSafe(top, bot, ty);
}

function lerpNullSafe(
  a: number | null,
  b: number | null,
  t: number,
): number | null {
  if (a === null && b === null) return null;
  if (a === null) return b;
  if (b === null) return a;
  return a * (1 - t) + b * t;
}

/**
 * Возвращает индекс ближайшей точки в данных. Используется для tooltip-а
 * на mousemove — показываем значение конкретной comune, а не интерполяцию,
 * чтобы при переключении на choropleth-вид юзер видел те же числа.
 */
export function nearestPoint(
  lat: number,
  lon: number,
  index: IdwIndex,
  maxRadiusDeg = 0.5,
): number | null {
  const { tree, points, lonScale } = index;
  const qx = lat;
  const qy = lon * lonScale;
  const ids = tree.within(qx, qy, maxRadiusDeg);
  if (ids.length === 0) return null;
  let bestId = -1;
  let bestD = Infinity;
  for (const id of ids) {
    const p = points[id];
    const dx = p.lat - lat;
    const dy = (p.lon - lon) * lonScale;
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      bestId = id;
    }
  }
  return bestId === -1 ? null : bestId;
}

/** Hex (#aabbcc) → [r, g, b]. */
export function hexToRgb(hex: string): [number, number, number] {
  const m = hex.match(/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return [128, 128, 128];
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}
